/**
 * CRM Client Financials Page - Financial summary, P&L overview, key metrics.
 * Available for Standard and Premium tier clients.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight,
  BarChart3, Loader2, Lock, PieChart,
} from 'lucide-react';
import { crmPortalService } from '@/services/api/crm-portal';
import { crmAuth } from '@/services/auth/crm-auth';
import { cn } from '@/lib/utils';

function fmtCurrency(amount: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount);
}

function fmtPercent(value: number) {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

export function CRMFinancialsPage() {
  const client = crmAuth.getClient();
  const [period, setPeriod] = useState('ytd');

  const { data, isLoading } = useQuery({
    queryKey: ['crm-financials', period],
    queryFn: () => crmPortalService.getFinancials(period),
  });

  const tier = client?.subscription_tier || 'free';
  const hasAccess = tier === 'standard' || tier === 'premium';

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Lock className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Financial Insights</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Upgrade to the Standard or Premium plan to access your financial summary,
          P&L overview, and business health metrics.
        </p>
        <Button onClick={() => window.location.href = '/crm/subscription'} className="bg-[#5048e5] hover:bg-[#443fcc]">
          View Plans
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  const fin = data?.financials;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Financial Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Your business at a glance</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ytd">Year to Date</SelectItem>
            <SelectItem value="last_quarter">Last Quarter</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="last_year">Last Financial Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {fin ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPICard title="Revenue" value={fmtCurrency(fin.revenue || 0)} change={fin.revenueChange} icon={DollarSign} />
            <KPICard title="Expenses" value={fmtCurrency(fin.expenses || 0)} change={fin.expensesChange} icon={BarChart3} invertColor />
            <KPICard title="Net Profit" value={fmtCurrency(fin.netProfit || 0)} change={fin.profitChange} icon={TrendingUp} />
            <KPICard title="Profit Margin" value={`${(fin.profitMargin || 0).toFixed(1)}%`} change={fin.marginChange} icon={PieChart} />
          </div>

          {/* Expense Breakdown */}
          {fin.topExpenses && fin.topExpenses.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Top Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {fin.topExpenses.map((exp: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{exp.name}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-[#5048e5] rounded-full" style={{ width: `${exp.percent}%` }} />
                        </div>
                        <span className="font-medium w-24 text-right">{fmtCurrency(exp.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Key Metrics */}
          {fin.metrics && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Key Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {fin.metrics.map((metric: any, i: number) => (
                    <div key={i} className="p-3 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground">{metric.label}</p>
                      <p className="text-lg font-semibold mt-0.5">{metric.value}</p>
                      {metric.description && <p className="text-[10px] text-muted-foreground mt-0.5">{metric.description}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart3 className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No financial data available yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Your accountant will connect your financial data soon.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KPICard({ title, value, change, icon: Icon, invertColor }: {
  title: string; value: string; change?: number; icon: any; invertColor?: boolean;
}) {
  const isPositive = change !== undefined && (invertColor ? change < 0 : change > 0);
  const isNegative = change !== undefined && (invertColor ? change > 0 : change < 0);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-muted-foreground">{title}</p>
          <Icon className="h-4 w-4 text-muted-foreground/50" />
        </div>
        <p className="text-xl font-bold">{value}</p>
        {change !== undefined && (
          <div className={cn('flex items-center gap-1 mt-1 text-xs font-medium',
            isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-muted-foreground'
          )}>
            {isPositive ? <ArrowUpRight className="h-3 w-3" /> : isNegative ? <ArrowDownRight className="h-3 w-3" /> : null}
            {fmtPercent(change)} vs prior period
          </div>
        )}
      </CardContent>
    </Card>
  );
}
